import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_CONFIG, DEBUG } from '@/config/api-config';

// API Configuration based on the integration guide
const API_BASE_URL = API_CONFIG.BASE_URL;

// API Response interfaces - Updated to match your backend format
export interface ApiResponse<T = any> {
  success?: boolean; // Keep for compatibility
  status?: boolean;  // Your API uses 'status' instead of 'success'
  message: string;
  data?: T;
  errors?: Record<string, string>;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface ApiError {
  success: false;
  message: string;
  errors?: Record<string, string>;
  code?: string;
}

// User and Auth interfaces
export interface User {
  id: string;
  name: string;
  phone: string;
  email: string;
  language: 'en' | 'ar';
  roleId: 1 | 2 | 3; // 1=customer, 2=admin, 3=staff
  token?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface LoginRequest {
  phone: string; // Saudi phone format: +966XXXXXXXXX
  password: string;
}

export interface LoginResponse {
  user?: User;
  token?: string | {
    access: string;
    refresh: string;
  };
  data?: {
    user: User;
    token: string;
  };
  // Allow for any additional properties the server might send
  [key: string]: any;
}

// API Client class
class ApiClient {
  private client: AxiosInstance;
  private token: string | null = null;

  constructor() {
    // Create headers - we'll set Content-Type per request type
    // Don't set Content-Type in default headers to allow FormData to work
    const defaultHeaders: Record<string, string> = {
      'Accept': API_CONFIG.DEFAULT_HEADERS.Accept,
      // Don't set Content-Type here - it will be set per request
    };
    
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
      headers: defaultHeaders,
    });

    if (DEBUG) {
      console.log('🔗 API Client initialized:', {
        baseURL: API_BASE_URL,
        timeout: API_CONFIG.TIMEOUT,
        useMockData: API_CONFIG.USE_MOCK_DATA
      });
    }

    // Load token from localStorage
    this.token = localStorage.getItem('authToken');
    if (this.token) {
      this.setAuthToken(this.token);
    }

    // Response interceptor for error handling and response transformation
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        if (DEBUG) {
          console.log('✅ API Response:', {
            url: response.config.url,
            status: response.status,
            data: response.data
          });
        }

        // Transform response to match ApiResponse format
        if (response.status >= 200 && response.status < 300) {
          // For successful responses, ensure we have the expected format
          if (response.data && typeof response.data === 'object') {
            // Handle your API format: { "status": true, "message": "...", "data": {...} }
            if ('status' in response.data) {
              // Check if the API returned status: false (API-level error)
              if (response.data.status === false) {
                if (DEBUG) {
                  console.error('❌ API returned status: false:', response.data);
                  console.error('❌ HTTP Status was:', response.status);
                  console.error('❌ Full response:', response);
                }
                // Treat this as an error even though HTTP status is 200
                const error = new Error(response.data.message || 'API Error');
                (error as any).response = {
                  status: response.status,
                  data: response.data
                };
                throw error;
              }
              
              const transformedData = {
                success: response.data.status, // Convert 'status' to 'success'
                data: response.data.data,      // Extract nested data
                message: response.data.message || 'Success',
                statusCode: response.status
              };
              
              if (DEBUG) {
                console.log('🔄 Transformed your API response:', {
                  original: response.data,
                  transformed: transformedData
                });
              }
              
              response.data = transformedData;
              return response;
            }
            
            // If the response already has success field, keep it
            if ('success' in response.data) {
              return response;
            }
            
            // Transform raw server response to ApiResponse format (fallback)
            const transformedData = {
              success: true,
              data: response.data,
              message: response.data.message || 'Success',
              statusCode: response.status
            };
            
            if (DEBUG) {
              console.log('🔄 Transformed response:', transformedData);
            }
            
            response.data = transformedData;
          }
        }
        
        return response;
      },
      (error) => {
        if (DEBUG) {
          console.error('❌ API Error:', {
            url: error.config?.url,
            status: error.response?.status,
            message: error.message,
            data: error.response?.data
          });
        }

        // Transform error response to ApiResponse format
        if (error.response) {
          // Extract detailed error information
          let errorMessage = 'Request failed';
          let errorDetails = null;
          
          if (error.response.data) {
            if (typeof error.response.data === 'string') {
              errorMessage = error.response.data;
            } else if (error.response.data.message) {
              errorMessage = error.response.data.message;
              errorDetails = error.response.data.errors;
            } else if (error.response.data.error) {
              errorMessage = error.response.data.error;
            }
          }
          
          const errorData = {
            success: false,
            data: null,
            message: errorMessage,
            statusCode: error.response?.status || 500,
            errors: errorDetails || error.response?.data?.errors || [],
            originalError: error.response.data // Keep original for debugging
          };
          
          if (DEBUG) {
            console.log('🔄 Transformed error response:', {
              original: error.response.data,
              transformed: errorData
            });
          }
          
          error.response.data = errorData;
        }

        if (error.response?.status === 401) {
          // Token expired or invalid
          console.warn('🔐 Authentication failed - redirecting to login');
          this.clearAuth();
          window.location.href = '/login';
        }
        
        return Promise.reject(error);
      }
    );

    // Request interceptor for language header and logging
    this.client.interceptors.request.use((config) => {
      const language = localStorage.getItem('language') || 'en';
      config.headers['Accept-Language'] = language;
      
      // Don't set Content-Type for FormData - let browser/axios set it with boundary
      if (config.data instanceof FormData) {
        // Remove any Content-Type header to let browser set it with boundary
        delete config.headers['Content-Type'];
      }
      
      if (DEBUG) {
        const isFormData = config.data instanceof FormData;
        console.log('📡 API Request:', {
          method: config.method?.toUpperCase(),
          url: config.url,
          baseURL: config.baseURL,
          fullURL: `${config.baseURL}${config.url}`,
          hasAuth: !!config.headers['Authorization'],
          authToken: config.headers['Authorization'] || 'MISSING',
          isFormData: isFormData,
          contentType: config.headers['Content-Type'],
          headers: config.headers,
          data: isFormData ? '[FormData]' : config.data
        });
        
        if (isFormData) {
          console.log('📡 FormData entries:');
          const entries = Array.from((config.data as FormData).entries());
          entries.forEach(([key, value]) => {
            if (value instanceof File) {
              console.log(`  - ${key}: File(${value.name}, ${value.size} bytes)`);
            } else {
              console.log(`  - ${key}: ${value}`);
            }
          });
        }
      }
      
      return config;
    });
  }

  // Set authentication token
  setAuthToken(token: string) {
    this.token = token;
    this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('authToken', token);
  }

  // Clear authentication
  clearAuth() {
    this.token = null;
    delete this.client.defaults.headers.common['Authorization'];
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }

  // Generic API methods
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.get<ApiResponse<T>>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    // Set Content-Type for JSON requests, but not for FormData
    const requestConfig: AxiosRequestConfig = { ...config };
    if (data && !(data instanceof FormData)) {
      requestConfig.headers = {
        ...requestConfig?.headers,
        'Content-Type': 'application/json',
      };
    }
    const response = await this.client.post<ApiResponse<T>>(url, data, requestConfig);
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.put<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.patch<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.delete<ApiResponse<T>>(url, config);
    return response.data;
  }

  // File upload method
  async upload<T>(url: string, formData: FormData, onProgress?: (progress: number) => void): Promise<ApiResponse<T>> {
    const response = await this.client.post<ApiResponse<T>>(url, formData, {
      // Axios automatically sets Content-Type to multipart/form-data with boundary for FormData
      // Don't set it manually to avoid issues
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
    return response.data;
  }

  // Authentication methods
  async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    const response = await this.post<LoginResponse>('/auth/login', credentials);
    
    if (DEBUG) {
      console.log('🔐 Login method response:', {
        success: response.success,
        hasData: !!response.data,
        dataKeys: response.data ? Object.keys(response.data) : [],
        response: response
      });
    }
    
    // Handle new response structure: { success: true, data: { user: {...}, token: { access: "...", refresh: "..." } } }
    if (response.success && response.data) {
      // Extract user and tokens from response.data (which contains { user, token })
      const user = response.data.user;
      const tokenData = response.data.token;
      
      // Extract access and refresh tokens
      const accessToken = tokenData?.access || tokenData;
      const refreshToken = tokenData?.refresh;
      
      // Store access token (used for API calls)
      if (accessToken) {
        this.setAuthToken(accessToken);
        if (DEBUG) console.log('🔐 Access token set:', accessToken.substring(0, 20) + '...');
      }
      
      // Store refresh token separately
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
        if (DEBUG) console.log('🔐 Refresh token stored');
      }
      
      // Store user data
      if (user && (user.id || user.name || user.phone)) {
        localStorage.setItem('user', JSON.stringify(user));
        console.log('🔐 User data stored to localStorage:', user);
        console.log('🔐 LocalStorage after storing:', {
          user: localStorage.getItem('user'),
          authToken: localStorage.getItem('authToken'),
          refreshToken: localStorage.getItem('refreshToken')
        });
      } else {
        console.log('🔐 No user data to store:', { user, hasId: !!(user?.id), hasName: !!(user?.name) });
      }
    }
    
    return response;
  }

  // Register customer (public - default)
  async register(userData: {
    name: string;
    phone: string;
    email: string;
    password: string;
    language?: 'en' | 'ar';
  }): Promise<ApiResponse<LoginResponse>> {
    if (DEBUG) {
      console.log('👤 Customer Registration:', { phone: userData.phone, name: userData.name });
    }
    return this.post<LoginResponse>('/auth/register', userData);
  }

  // Register customer (explicit)
  async registerCustomer(userData: {
    name: string;
    phone: string;
    email: string;
    password: string;
    language?: 'en' | 'ar';
  }): Promise<ApiResponse<LoginResponse>> {
    if (DEBUG) {
      console.log('👤 Customer Registration (explicit):', { phone: userData.phone });
    }
    return this.post<LoginResponse>('/auth/register/customer', userData);
  }

  // Register staff (direct)
  async registerStaff(userData: {
    name: string;
    phone: string;
    email: string;
    password: string;
    language?: 'en' | 'ar';
  }): Promise<ApiResponse<LoginResponse>> {
    if (DEBUG) {
      console.log('👨‍💼 Staff Registration:', { phone: userData.phone });
    }
    return this.post<LoginResponse>('/auth/register/staff', userData);
  }

  // Register admin (direct)
  async registerAdmin(userData: {
    name: string;
    phone: string;
    email: string;
    password: string;
    language?: 'en' | 'ar';
  }): Promise<ApiResponse<LoginResponse>> {
    if (DEBUG) {
      console.log('👨‍💼 Admin Registration:', { phone: userData.phone });
    }
    return this.post<LoginResponse>('/auth/register/admin', userData);
  }

  async logout(): Promise<void> {
    try {
      await this.post('/auth/logout');
    } catch (error) {
      // Continue with logout even if API call fails
      console.warn('Logout API call failed:', error);
    } finally {
      this.clearAuth();
    }
  }

  async refreshToken(): Promise<ApiResponse<{ token: string }>> {
    return this.post<{ token: string }>('/auth/refresh');
  }

  async getCurrentUser(): Promise<ApiResponse<User>> {
    return this.get<User>('/auth/me');
  }

  // Utility methods
  isAuthenticated(): boolean {
    // Simple check without logging to prevent loops
    const hasToken = !!this.token;
    const hasStoredUser = !!localStorage.getItem('user');
    const hasAuthToken = !!localStorage.getItem('authToken');
    
    return hasToken || hasAuthToken || hasStoredUser;
  }

  getStoredUser(): User | null {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  // Language management
  setLanguage(language: 'en' | 'ar') {
    localStorage.setItem('language', language);
  }

  getLanguage(): 'en' | 'ar' {
    return (localStorage.getItem('language') as 'en' | 'ar') || 'en';
  }
}

// Create and export singleton instance
export const apiClient = new ApiClient();
export default apiClient;
